import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { FacetsPage } from 'src/app/facets/facets-page/facets-page.component';
import { ActivatedRoute, Params } from '@angular/router';
import {
  UntypedFormControl,
  UntypedFormGroup,
  FormGroupDirective,
} from '@angular/forms';
import { Options } from './single-options.model';

@Component({
  selector: 'app-select-single-search',
  templateUrl: './select-single-search.component.html',
  styleUrls: ['./select-single-search.component.scss'],
  providers: [FormGroupDirective],
})
export class SelectSingleSearchComponent implements OnChanges {
  //events
  @Output() selectedOptionEvent = new EventEmitter<any>();
  @Output() removeOptionEvent = new EventEmitter<string>();
  @Output() infoClick = new EventEmitter<any>();

  //inputs
  @Input() options: Options[] = [];
  @Input() optionsOriginal: Options[] = [];
  @Input() searchInputId: string = '';
  @Input() selectInputPlaceholder: string = 'Select options';
  @Input() searchInputPlaceholder: string = 'Search options..';
  @Input() selectInputLabel: string = '';
  @Input() multiple: boolean = false;
  @Input() required: boolean = false;
  @Input() showClearAllOptionsIcon: boolean = true;
  @Input() selectTooltipText: string = '';
  @Input() showLimitedTags: boolean = false;
  @Input() tagLimit: number = 2;
  @Input() showSelectionsOutside: boolean = true;
  @Input() inputColSize: number = 5;
  @Input() labelColSize: number = 3;
  @Input() showSpinner: boolean = false;
  @Input() selectionHeight: string = '15vh';
  @Input() showRemovableOptionTags: boolean = true;
  @Input() showCombinedText: boolean = false;
  @Input() disableSelectInput: boolean = false;
  @Input() selectionTextLabel: string = '';
  @Input() disabledInputTooltip: string = '';
  @Input() showDisabledInputTooltip: boolean = false;
  @Input() showUserComponent: boolean = false;
  @Input() showSuffixIcon: boolean = false;
  @Input() showSelectAll: boolean = false;
  @Input() parentClass: string = '';
  @Input() showTags: boolean = true;
  @Input() showInfoIcon: boolean = false;
  @Input() infoIcon: string = 'info-outline';
  @Input() infoIconStatus = 'primary';
  @Input() showClearAllOption: boolean = false;
  @Input() showInfoAsTooltip: boolean = true;
  @Input() initialSelectionOrder: any = [];
  @Input() showSearchBar: boolean = true;
  @Input() formMargin: number = 3;

  //local variables
  selectedOptionsList: {
    label: string;
    value: string;
    icon?: string;
    iconPack?: string;
  }[] = [];
  openOptionsPopover: boolean = false;
  selectAllControl: UntypedFormControl = new UntypedFormControl(false);
  searchString: UntypedFormControl = new UntypedFormControl('');

  // Add new property to track selection order
  private selectionOrder: string[] = [];

  constructor(private activatedRoute: ActivatedRoute) {}

  OnPageInit(): void {}

  OnPageLoad(p: Params): void {}

  ngOnChanges(changes: SimpleChanges) {
    this.selectedOptionsList = [];
    this.selectionOrder = []; // Reset selection order
    this.showSpinner = changes?.showSpinner?.currentValue || false;
    if (changes?.options?.currentValue) {
      this.optionsOriginal = changes?.options?.currentValue;
      // If initialSelectionOrder is provided, use it to maintain order
      if (
        this.initialSelectionOrder &&
        this.initialSelectionOrder?.length > 0
      ) {
        // First mark all options as checked/unchecked
        this.optionsOriginal?.forEach((option) => {
          option.checked = this.initialSelectionOrder?.includes(option?.value);
        });

        // Then build the lists in the correct order
        this.selectionOrder = [...this.initialSelectionOrder];
        this.selectedOptionsList = this.initialSelectionOrder?.map((value) => {
          const option = this.optionsOriginal?.find(
            (opt) => opt?.value === value,
          );
          return {
            label: option?.label,
            value: option?.value,
          };
        });
      } else {
        // Fallback to original behavior if no initialSelectionOrder is provided
        changes?.options?.currentValue?.forEach((option) => {
          if (option?.checked) {
            this.selectedOptionsList?.push({
              label: option?.label,
              value: option?.value,
            });
            this.selectionOrder?.push(option?.value);
          }
        });
      }
      this.setSelectAllCheckboxStatus();
    }
  }

  changeSelectOptionValue(event, selectedOption) {
    if (!this.multiple) {
      // Handle single selection case
      this.selectedOptionsList = [];
      this.selectionOrder = [];
      this.options?.forEach((option) => {
        if (option?.value !== selectedOption?.value) {
          option.checked = false;
        }
      });
    }

    // Update selection order based on the event
    if (event) {
      // Option was checked
      this.selectionOrder.push(selectedOption.value);
    } else {
      // Option was unchecked
      this.selectionOrder = this.selectionOrder.filter(
        (value) => value !== selectedOption.value,
      );
    }

    this.rebuildSelectionOrderList();
    this.emitOptionEvents();
  }

  /*
      Filter function
    */
  getFilteredOptions() {
    if (this.searchString.value?.length > 0) {
      this.options = [
        ...this.searchOptions(
          this.optionsOriginal,
          'label',
          this.searchString.value,
        ),
      ];
    } else {
      this.options = [...this.optionsOriginal];
    }
  }

  searchOptions(options, key, searchStr) {
    // Use Array.filter() to only keep objects whose labels match the search string
    const filteredArr = options.filter((option) =>
      option?.[key]?.toLowerCase().includes(searchStr?.toLowerCase()),
    );
    return filteredArr;
  }

  removeClicked(filterOption) {
    if (this.options) {
      this.options.find(
        (option) => option?.value === filterOption?.value,
      ).checked = false;
    }

    // Remove from selection order
    this.selectionOrder = this.selectionOrder.filter(
      (value) => value !== filterOption.value,
    );

    this.rebuildSelectionOrderList();
    this.emitOptionEvents();
    this.removeOptionEvent.emit(filterOption);
  }

  rebuildSelectionOrderList() {
    // Rebuild selectedOptionsList based on selection order
    this.selectedOptionsList = this.selectionOrder.map((value) => {
      const option = this.optionsOriginal.find((opt) => opt.value === value);
      return {
        label: option?.label,
        value: option?.value,
      };
    });
  }

  /*
   * Clear all selected options when user clicks on close button on top level select input
   */
  clearAllSelectedOptions() {
    this.selectedOptionsList = [];
    this.selectionOrder = []; // Clear selection order
    this.emitOptionEvents();
    this.options?.forEach((option) => (option.checked = false));
  }

  setSelectAllCheckboxStatus() {
    this.selectAllControl.setValue(
      this.options?.every((option) => option.checked),
    );
  }

  emitOptionEvents() {
    this.setSelectAllCheckboxStatus();
    this.selectedOptionEvent.emit(this.selectedOptionsList);
  }

  showStateChange(event) {
    this.openOptionsPopover = event.isShown;
    if (!event.isShown) {
      this.searchString.setValue('');
      this.options = [...this.optionsOriginal];
    } else {
      this.openOptionsPopover = true;
      let selectBoundingRect = document
        .querySelector('.input-parent')
        .getBoundingClientRect();
      let popoverElem = document.querySelector('nb-popover');
      let dropdownElement = popoverElem?.parentElement;
      if (dropdownElement) {
        dropdownElement.style.width = selectBoundingRect.width + 'px';
        dropdownElement.style.left = selectBoundingRect.left + 'px';
      }
    }
  }

  onSelectAllClick(event) {
    this.selectedOptionsList = [];
    this.options.forEach((option) => {
      option.checked = event;
    });
    this.optionsOriginal.forEach((option) => {
      if (option?.checked) {
        this.selectedOptionsList.push({
          label: option?.label,
          value: option?.value,
        });
      }
    });
    this.emitOptionEvents();
    this.searchString.setValue('');
  }

  onInfoBtnClick(event, option) {
    this.infoClick.emit(option);
    event.preventDefault();
  }
}
